import type { ProjectRepository } from '@/domain/project/ProjectRepository'

/**
 * Single source of truth for telemetry consent (PRD §"Telemetry",
 * tech-spec §12.3, ADR-024).
 *
 * The store reads the persisted value from IDB on `load()` and keeps
 * a synchronous cached copy so the {@link ConsentingTelemetryAdapter}
 * can decide drop-or-emit without awaiting on every event. Subscribers
 * (the React provider) re-render when the value changes.
 *
 * Default is `false` (opt-in required). A missing IDB value resolves
 * to `false` — never to "unknown" — because emitting on uncertainty
 * would breach the privacy contract.
 */

export const TELEMETRY_CONSENT_META_KEY = 'telemetryConsent'
export const TELEMETRY_CONSENT_DISMISSED_AT_META_KEY = 'telemetryConsentDismissedAt'

export class TelemetryConsentStore {
  private consent = false
  private dismissedAt: number | null = null
  private loaded = false
  private readonly listeners = new Set<() => void>()
  private readonly repo: ProjectRepository

  constructor(repo: ProjectRepository) {
    this.repo = repo
  }

  async load(): Promise<void> {
    const [consent, dismissedAt] = await Promise.all([
      this.repo.getMeta<boolean>(TELEMETRY_CONSENT_META_KEY),
      this.repo.getMeta<number>(TELEMETRY_CONSENT_DISMISSED_AT_META_KEY),
    ])
    this.consent = consent === true
    this.dismissedAt = typeof dismissedAt === 'number' ? dismissedAt : null
    this.loaded = true
    this.notify()
  }

  isLoaded(): boolean {
    return this.loaded
  }

  isConsenting(): boolean {
    return this.consent
  }

  wasDismissed(): boolean {
    return this.dismissedAt !== null
  }

  async set(value: boolean): Promise<void> {
    const now = Date.now()
    this.consent = value
    this.dismissedAt = now
    await Promise.all([
      this.repo.setMeta(TELEMETRY_CONSENT_META_KEY, value),
      this.repo.setMeta(TELEMETRY_CONSENT_DISMISSED_AT_META_KEY, now),
    ])
    this.notify()
  }

  /**
   * Records that the user has dismissed the consent prompt without
   * granting consent. `consent` stays at its current value (typically
   * `false`); `dismissedAt` becomes a timestamp. ADR-035 — dismissal
   * persists; no re-asks.
   */
  async dismiss(): Promise<void> {
    const now = Date.now()
    this.dismissedAt = now
    await this.repo.setMeta(TELEMETRY_CONSENT_DISMISSED_AT_META_KEY, now)
    this.notify()
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notify(): void {
    for (const listener of this.listeners) listener()
  }
}
